import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const luxuryNoir: TemplateRegistration = {
  meta: {
    id: 'luxury-noir',
    name: 'Luxury Noir',
    category: 'Classic',
    description: 'Premium dark design with gold accents for high-end brands',
    previewImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
    themeEditable: false,
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(2, 7);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Cinzel', 'Cormorant']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-inner">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <p>${rating.toFixed(1)} &bull; ${reviewCount} reviews</p>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-bar">
        <div class="container">
          <span class="promo-discount">${promoDiscount}</span>
          <span class="promo-divider">&mdash;</span>
          <span class="promo-title">${promoTitle}</span>
          <span class="promo-desc">${promoDesc}</span>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section">
        <div class="container">
          <p class="section-label">About</p>
          <div class="gold-line"></div>
          <div class="about-layout">
            <div class="about-pullquote">&ldquo;${truncateAtSentence(safeAbout, 120)}&rdquo;</div>
            <div class="about-text-col">
              <h2>${safeName}</h2>
              <p class="about-body">${safeAbout}</p>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon-circle">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <p class="section-label">Services</p>
          <div class="gold-line"></div>
          <h2 class="section-heading">What We Offer</h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <p class="section-label">Gallery</p>
          <div class="gold-line"></div>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <span class="quote-mark">&ldquo;</span>
            <p class="testimonial-text">${tContent}</p>
            <div class="testimonial-footer">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="t-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <p class="section-label">Testimonials</p>
          <div class="gold-line"></div>
          <h2 class="section-heading">Client Experiences</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3>${q}</h3>
            <p>${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <p class="section-label">FAQ</p>
          <div class="gold-line"></div>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-dots"></span>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <p class="section-label">Pricing</p>
          <div class="gold-line"></div>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <p class="section-label">Hours</p>
          <div class="gold-line"></div>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <p class="section-label">Contact</p>
          <div class="gold-line"></div>
          <h2 class="section-heading">Get In Touch</h2>
          <div class="contact-layout">
            <div class="contact-details">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Map &rarr;</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Full Name" required>
              <input type="email" placeholder="Email Address" required>
              <textarea placeholder="Your Message" rows="5" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #0a0a0a; --bg-alt: #111111; --gold: #c9a96e; --gold-dim: rgba(201,169,110,.2);
      --cream: #f5f0e8; --cream-muted: #bdb5a8; --font-heading: 'Cinzel', serif; --font-body: 'Cormorant', serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--cream); line-height: 1.7; font-size: 18px; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--gold); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(10,10,10,.95); backdrop-filter: blur(12px); border-bottom: 1px solid var(--gold-dim); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; max-width: 1100px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.1rem; color: var(--gold); text-transform: uppercase; letter-spacing: 4px; }
    .nav-links { display: flex; gap: 32px; list-style: none; }
    .nav-links a { color: var(--cream-muted); font-family: var(--font-heading); font-size: .7rem; text-transform: uppercase; letter-spacing: 2.5px; }
    .nav-links a:hover { color: var(--gold); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 1px; background: var(--gold); margin: 6px 0; }

    /* HERO */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center; overflow: hidden; background: linear-gradient(135deg, var(--bg) 0%, #1a1005 25%, #0d0a06 50%, var(--bg-alt) 75%, var(--bg) 100%); }
    .hero-gradient-overlay { position: absolute; inset: 0; background: linear-gradient(45deg, rgba(201,169,110,.08) 0%, transparent 40%, rgba(201,169,110,.05) 70%, transparent 100%); z-index: 1; }
    .hero-content { position: relative; z-index: 2; max-width: 1100px; margin: 0 auto; width: 100%; padding: 0 24px; text-align: center; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.2rem, 5vw, 3.8rem); font-weight: 400; text-transform: uppercase; letter-spacing: 6px; line-height: 1.2; margin-bottom: 20px; color: var(--cream); }
    .hero-ornament { display: flex; align-items: center; justify-content: center; gap: 16px; margin-bottom: 24px; }
    .hero-ornament .line { width: 60px; height: 1px; background: var(--gold); }
    .hero-ornament .diamond { width: 8px; height: 8px; background: var(--gold); transform: rotate(45deg); }
    .hero-sub { font-size: 1.1rem; color: var(--cream-muted); max-width: 560px; margin: 0 auto 36px; }
    .hero-cta { display: inline-block; padding: 14px 44px; border: 1px solid var(--gold); color: var(--gold); font-family: var(--font-heading); font-size: .75rem; text-transform: uppercase; letter-spacing: 3px; transition: background .3s, color .3s; }
    .hero-cta:hover { background: var(--gold); color: var(--bg); opacity: 1; }

    /* SECTIONS */
    .section { padding: 96px 0; border-top: 1px solid var(--gold-dim); }
    .section-alt { background: var(--bg-alt); }
    .section-label { font-family: var(--font-heading); font-size: .7rem; text-transform: uppercase; letter-spacing: 4px; color: var(--gold); margin-bottom: 12px; }
    .gold-line { width: 40px; height: 1px; background: var(--gold); margin-bottom: 32px; }
    .section-heading { font-family: var(--font-heading); font-size: clamp(1.6rem, 3vw, 2.4rem); font-weight: 400; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 48px; color: var(--cream); }

    /* ABOUT */
    .about-layout { display: grid; grid-template-columns: 1fr 1.2fr; gap: 64px; align-items: center; }
    .about-pullquote { font-family: var(--font-heading); font-size: 1.6rem; font-weight: 400; font-style: italic; color: var(--gold); line-height: 1.6; border-left: 2px solid var(--gold-dim); padding-left: 24px; }
    .about-text-col h2 { font-family: var(--font-heading); font-size: 1.8rem; font-weight: 400; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 24px; }
    .about-body { color: var(--cream-muted); font-size: 1.05rem; line-height: 1.9; }

    /* SERVICES */
    .services-grid { display: flex; flex-direction: column; gap: 0; }
    .service-card { display: flex; align-items: center; gap: 24px; padding: 32px 0; border-bottom: 1px solid var(--gold-dim); transition: border-color .3s; }
    .service-card:last-child { border-bottom: none; }
    .service-card:hover { border-color: var(--gold); }
    .service-icon-circle { width: 48px; height: 48px; border-radius: 50%; border: 1px solid var(--gold); display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: var(--gold); }
    .service-icon-circle .icon-char { font-size: 20px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: .9rem; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; color: var(--cream); }
    .service-card p { color: var(--cream-muted); font-size: .95rem; line-height: 1.7; }
    .service-price { display: inline-block; margin-top: 8px; color: var(--gold); font-size: 1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; }
    .gallery-item img { width: 100%; height: 260px; object-fit: cover; display: block; border: 1px solid var(--gold-dim); transition: opacity .3s; }
    .gallery-item img:hover { opacity: .85; }

    /* TESTIMONIALS */
    .testimonials-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 40px; }
    .testimonial-card { padding: 40px; border: 1px solid var(--gold-dim); position: relative; }
    .quote-mark { font-family: var(--font-heading); font-size: 4rem; color: var(--gold); line-height: 1; position: absolute; top: 16px; left: 24px; opacity: .5; }
    .testimonial-text { font-size: 1.05rem; font-style: italic; color: var(--cream-muted); line-height: 1.8; margin-bottom: 24px; padding-top: 24px; }
    .testimonial-footer strong { display: block; font-family: var(--font-heading); font-size: .8rem; text-transform: uppercase; letter-spacing: 2px; color: var(--cream); margin-bottom: 4px; }
    .testimonial-footer span { color: var(--cream-muted); font-size: .85rem; }
    .t-stars { margin-top: 8px; }
    .star { color: #333; font-size: .95rem; }
    .star.filled { color: var(--gold); }

    /* GOOGLE REVIEWS */
    .google-reviews { padding: 24px 0; border-top: 1px solid var(--gold-dim); text-align: center; }
    .review-inner { display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap; }
    .review-inner p { color: var(--cream-muted); font-size: .9rem; }

    /* PROMO */
    .promo-bar { padding: 20px 0; background: var(--gold); text-align: center; }
    .promo-bar .container { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .promo-discount { font-family: var(--font-heading); font-weight: 700; color: var(--bg); text-transform: uppercase; letter-spacing: 2px; }
    .promo-divider { color: var(--bg); opacity: .5; }
    .promo-title { font-family: var(--font-heading); color: var(--bg); font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; }
    .promo-desc { color: rgba(10,10,10,.7); font-size: .85rem; }

    /* FAQ */
    .faq-list { max-width: 720px; }
    .faq-item { padding: 28px 0; border-bottom: 1px solid var(--gold-dim); }
    .faq-item h3 { font-family: var(--font-heading); font-size: .9rem; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 12px; color: var(--cream); }
    .faq-item p { color: var(--cream-muted); font-size: .95rem; line-height: 1.8; }

    /* PRICE LIST */
    .price-list { max-width: 720px; }
    .price-row { display: flex; align-items: baseline; gap: 16px; padding: 24px 0; border-bottom: 1px solid var(--gold-dim); }
    .price-info { flex-shrink: 0; }
    .price-info h3 { font-family: var(--font-heading); font-size: .85rem; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 4px; color: var(--cream); }
    .price-info p { color: var(--cream-muted); font-size: .85rem; }
    .price-dots { flex: 1; border-bottom: 1px dotted var(--gold-dim); margin-bottom: 4px; }
    .price-amount { font-family: var(--font-heading); font-size: 1.2rem; color: var(--gold); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid var(--gold-dim); color: var(--cream-muted); font-size: .95rem; }

    /* CONTACT */
    .contact-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: start; }
    .contact-details { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--cream-muted); }
    .contact-item .icon-char { color: var(--gold); font-size: 20px; line-height: 1; flex-shrink: 0; }
    .contact-item a { color: var(--cream); }
    .map-link { display: inline-block; margin-top: 8px; font-family: var(--font-heading); font-size: .7rem; text-transform: uppercase; letter-spacing: 2px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: transparent; border: 1px solid var(--gold-dim); color: var(--cream); padding: 14px 18px; font-family: var(--font-body); font-size: 1rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--gold); }
    .contact-form input::placeholder, .contact-form textarea::placeholder { color: var(--cream-muted); opacity: .6; }
    .contact-form button { padding: 14px; border: 1px solid var(--gold); background: transparent; color: var(--gold); font-family: var(--font-heading); font-size: .75rem; text-transform: uppercase; letter-spacing: 3px; cursor: pointer; transition: background .3s, color .3s; }
    .contact-form button:hover { background: var(--gold); color: var(--bg); }

    /* FOOTER */
    .footer { padding: 48px 0; text-align: center; border-top: 1px solid var(--gold-dim); }
    .footer-ornament { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 16px; }
    .footer-ornament .fl { width: 40px; height: 1px; background: var(--gold); opacity: .5; }
    .footer-ornament .fd { width: 6px; height: 6px; background: var(--gold); transform: rotate(45deg); opacity: .5; }
    .footer p { color: var(--cream-muted); font-size: .8rem; letter-spacing: 1px; }

    /* RESPONSIVE */
    @media (max-width: 900px) {
      .about-layout { grid-template-columns: 1fr; gap: 32px; }
      .contact-layout { grid-template-columns: 1fr; gap: 32px; }
    }
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(10,10,10,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
    }
    @media (max-width: 600px) {
      .testimonials-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 80px 16px 0; }
      .section { padding: 64px 0; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .price-row { flex-direction: column; gap: 8px; }
      .price-dots { display: none; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-gradient-overlay"></div>
    <div class="hero-content">
      <h1>${safeHeadline}</h1>
      <div class="hero-ornament">
        <span class="line"></span>
        <span class="diamond"></span>
        <span class="line"></span>
      </div>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Reserve Now</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <div class="footer-ornament">
        <span class="fl"></span><span class="fd"></span><span class="fl"></span>
      </div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
